import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { questionnaireApi, leadApi, customerApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DynamicForm from '../../components/DynamicForm.jsx';
import ScoreGauge from '../../components/ScoreGauge.jsx';
import Spinner from '../../components/Spinner.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';

/**
 * The teleprospection qualification script.
 * The questionnaire is fetched from the API in the active language, so the form
 * follows the user's locale without a single hard-coded question here.
 *
 * Three ways in, and they all end on the same form:
 *   /telepro/qualification                    - a company nobody had in the CRM
 *   /telepro/qualification?customerId=...     - calling an existing PROSPECT
 *   /telepro/qualification/:id                - re-opening a draft
 *
 * The middle one is the normal path: prospects are the call list, so the form
 * opens pre-filled with what we already know and the lead attaches to THAT
 * record - calling a prospect turns it into a qualification, it never creates a
 * second company with the same name.
 */
const QualificationFormPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();

  const locale = i18n.resolvedLanguage;
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [scoring, setScoring] = useState({ score: 0, temperature: 'COLD' });
  const [leadId, setLeadId] = useState(id ?? null);
  const [customerId, setCustomerId] = useState(searchParams.get('customerId') || null);
  const [prospect, setProspect] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: definition, loading } = useApi(() => questionnaireApi.telepro(locale), [locale]);

  // Editing an existing draft: load its answers once.
  useEffect(() => {
    if (!id) return;
    leadApi
      .get(id)
      .then((lead) => {
        setAnswers(lead.answers ?? {});
        setCustomerId(lead.customerId ?? null);
        setScoring({ score: lead.score ?? 0, temperature: lead.temperature ?? 'COLD' });
      })
      .catch((err) => toast.error(toMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Calling a prospect: open the script with what the record already holds, so
  // the telepro confirms details instead of re-typing them.
  useEffect(() => {
    const fromProspect = searchParams.get('customerId');
    if (id || !fromProspect) return;
    customerApi
      .get(fromProspect)
      .then((customer) => {
        setProspect(customer);
        setAnswers((current) => ({
          company_name: customer.companyName,
          contact_full_name: [customer.contactFirstName, customer.contactLastName].filter(Boolean).join(' '),
          contact_role: customer.contactRole || undefined,
          contact_email: customer.email || undefined,
          contact_phone: customer.phone || undefined,
          website: customer.website || undefined,
          industry: customer.industry || undefined,
          country: customer.country || undefined,
          headcount: customer.companySize || undefined,
          preferred_language: customer.locale || undefined,
          ...current,
        }));
      })
      .catch((err) => toast.error(toMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Live score: recomputed server-side so the rule stays in one place.
  useEffect(() => {
    if (!Object.keys(answers).length) return undefined;
    const timer = setTimeout(() => {
      questionnaireApi.score(answers).then(setScoring).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [answers]);

  const totals = useMemo(() => {
    if (!definition) return { answered: 0, total: 0 };
    const questions = definition.sections.flatMap((section) => section.questions);
    const answered = questions.filter((question) => {
      const value = answers[question.key];
      return value !== undefined && value !== '' && !(Array.isArray(value) && !value.length);
    }).length;
    return { answered, total: questions.length };
  }, [definition, answers]);

  const onChange = (key, value) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const persist = async (status) => {
    setBusy(true);
    setErrors({});
    try {
      // customerId is what stops a called prospect becoming a duplicate company.
      const payload = { answers, locale, status, ...(customerId ? { customerId } : {}) };
      const lead = leadId
        ? await leadApi.update(leadId, payload)
        : await leadApi.create(payload);

      setLeadId(lead.id);
      setCustomerId(lead.customerId ?? null);
      toast.success(status === 'SUBMITTED' ? t('leads.submitted') : t('common.save'));

      // A qualified call that ends on "book a meeting" goes straight to the agenda.
      if (status === 'SUBMITTED' && answers.outcome === 'meeting') {
        navigate(`/telepro/book?leadId=${lead.id}&customerId=${lead.customerId ?? ''}`);
      } else if (status === 'SUBMITTED') {
        navigate('/telepro/leads');
      }
    } catch (err) {
      if (Array.isArray(err.details)) {
        setErrors(Object.fromEntries(err.details.map((detail) => [detail.path, detail.message])));
        toast.error(toMessage(err));
        const first = document.getElementById(`q-${err.details[0].path}`);
        first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        toast.error(toMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner full />;

  return (
    <>
      <PageHeader
        title={prospect ? t('leads.callingProspect', { name: prospect.companyName }) : definition.title}
        subtitle={definition.description}
        actions={
          <div className="header-actions">
            <button type="button" className="btn" onClick={() => persist('DRAFT')} disabled={busy}>
              {t('leads.saveDraft')}
            </button>
            {/* Re-open the booking step for a call that was saved earlier. */}
            {leadId && answers.outcome === 'meeting' ? (
              <button
                type="button"
                className="btn"
                onClick={() => navigate(`/telepro/book?leadId=${leadId}&customerId=${customerId ?? ''}`)}
              >
                {t('leads.bookMeetingNow')}
              </button>
            ) : null}
            <button type="button" className="btn btn--primary" onClick={() => persist('SUBMITTED')} disabled={busy}>
              {busy
                ? t('common.saving')
                : answers.outcome === 'meeting'
                  ? t('leads.submitAndBook')
                  : t('leads.submit')}
            </button>
          </div>
        }
      />

      <div className="qualif-layout">
        <div className="qualif-layout__form">
          <DynamicForm definition={definition} answers={answers} onChange={onChange} errors={errors} />
        </div>

        <aside className="qualif-layout__aside">
          <Card title={t('leads.score')}>
            <ScoreGauge score={scoring.score} temperature={scoring.temperature} />
            <p className="muted small">
              {t('leads.progress', { answered: totals.answered, total: totals.total })}
            </p>
            <div className="progress">
              <span
                className="progress__fill"
                style={{ width: `${totals.total ? (totals.answered / totals.total) * 100 : 0}%` }}
              />
            </div>

            {/* Drives what the commercial will scope and price. */}
            {scoring.complexity ? (
              <>
                <div className="complexity">
                  <span className="complexity__label">{t('leads.complexity')}</span>
                  <Badge
                    value={scoring.complexity}
                    label={t(`leads.complexities.${scoring.complexity}`)}
                  />
                </div>
                <div className="complexity__drivers">
                  <span className="complexity__driver">
                    {t('leads.integrations')} <strong>{scoring.drivers?.integrationCount ?? 0}</strong>
                  </span>
                  <span className="complexity__driver">
                    {t('leads.autonomy')}{' '}
                    <strong>
                      {scoring.drivers?.autonomy
                        ? t(`leads.autonomies.${scoring.drivers.autonomy}`)
                        : '-'}
                    </strong>
                  </span>
                </div>
              </>
            ) : null}
          </Card>
        </aside>
      </div>
    </>
  );
};

export default QualificationFormPage;
